/**
 * OPTIONAL account: trusted devices + deferred-send mailbox.
 *
 * Guest-first intact: this screen is the ONLY door to the account and everything
 * else works without it. The "account" is anonymous (no email/password): a
 * key pair per device + a shareable address. The mailbox stores
 * END-TO-END ENCRYPTED ENVELOPES — they are decrypted here, on the device.
 */

import { createRoute } from "@tanstack/solid-router";
import { createSignal, For, onCleanup, onMount, Show } from "solid-js";
import {
  Check,
  CloudOff,
  Copy,
  Inbox,
  Laptop,
  RefreshCw,
  ScrollText,
  ShieldCheck,
  Trash2,
} from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import {
  ACCOUNT_ADDRESS_PREFIX,
  type AuditAction,
  type AuditEntry,
  DEVICE_LINK_CODE_PREFIX,
  parseDeviceLinkCode,
  type RemoteDevice,
} from "@enviar/core";
import { rootRoute } from "@/routes/__root";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { PageBackground } from "@/components/ui/page-background";
import { TopBar } from "@/components/ui/top-bar";
import {
  accountAddress,
  accountAuth,
  type CollectedFile,
  collectEnvelope,
  completeDeviceLink,
  createLocalAccount,
  forgetLocalAccount,
  loadLocalAccount,
  type LocalAccount,
  startDeviceLink,
} from "@/lib/mailbox/account";
import {
  addTrustedDevice,
  type EnvelopeMeta,
  fetchAudit,
  fetchDevices,
  listEnvelopes,
  MailboxApiError,
  removeTrustedDevice,
} from "@/lib/mailbox/client";

function formatBytes(n: number): string {
  if (n < 1024) {
    return `${n} B`;
  }
  if (n < 1024 * 1024) {
    return `${(n / 1024).toFixed(1)} KB`;
  }
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const panelClass =
  "flex w-full flex-col gap-4 rounded-[var(--radius-2xl)] border border-border bg-surface/70 p-6 shadow-md backdrop-blur";

const inputClass =
  "h-11 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * State of what lives IN THE RELAY (mailbox, devices, audit). It is not a
 * cosmetic detail: without it, "there are no pending deliveries" and "no activity" are
 * painted the same when the relay answered empty as when it did not answer — and a
 * mailbox with three envelopes waiting would look empty, and an account with four
 * trusted devices would look like it has none (read: "they have been
 * revoked from me"). The emptiness is an ASSERTION about the mailbox: it can only be painted
 * with a response in hand (P23/CC-60, I2 and I3).
 */
type RemoteState = "loading" | "ready" | "error";

function AccountPage() {
  const { t } = useI18n();
  const [account, setAccount] = createSignal<LocalAccount | null>(null);
  const [loaded, setLoaded] = createSignal(false);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string>();
  const [remote, setRemote] = createSignal<RemoteState>("loading");
  const [remoteError, setRemoteError] = createSignal<string>();

  // Enrollment / linking
  const [deviceName, setDeviceName] = createSignal("");
  const [linkCode, setLinkCode] = createSignal<string>();
  const [linkAddress, setLinkAddress] = createSignal("");

  // With account
  const [copied, setCopied] = createSignal(false);
  const [devices, setDevices] = createSignal<RemoteDevice[]>([]);
  const [pasteCode, setPasteCode] = createSignal("");
  const [inbox, setInbox] = createSignal<EnvelopeMeta[]>([]);
  const [collected, setCollected] = createSignal<CollectedFile[]>([]);
  const [audit, setAudit] = createSignal<AuditEntry[]>([]);

  const objectUrls: string[] = [];
  onCleanup(() => {
    for (const url of objectUrls) {
      URL.revokeObjectURL(url);
    }
  });

  const errorMessage = (err: unknown): string => {
    if (err instanceof MailboxApiError) {
      switch (err.kind) {
        case "rate-limited": {
          return t(msg`Too many attempts — try again in a few minutes.`);
        }
        case "not-found": {
          return t(msg`Nothing found for that address.`);
        }
        case "unauthorized": {
          return t(msg`This device is not authorized for that account.`);
        }
        case "invalid": {
          return t(msg`The mailbox rejected the request (quota or invalid data).`);
        }
        default: {
          return t(msg`The mailbox service is unreachable right now.`);
        }
      }
    }
    return t(msg`Something went wrong. Try again.`);
  };

  const refresh = async (acc: LocalAccount) => {
    setRemote("loading");
    setRemoteError(undefined);
    try {
      const [remoteDevices, envelopes, entries] = await Promise.all([
        fetchDevices(acc.accountId),
        listEnvelopes(acc.accountId, accountAuth(acc)),
        fetchAudit(acc.accountId, accountAuth(acc)),
      ]);
      setDevices(remoteDevices);
      setInbox(envelopes);
      setAudit(entries);
      setRemote("ready");
    } catch (err) {
      // It is announced IN THE SECTION (with retry) and also re-thrown: the
      // caller that came from a user action keeps notifying above.
      setRemoteError(errorMessage(err));
      setRemote("error");
      throw err;
    }
  };

  // Human-readable name of the device referred to by an audit entry
  // (denormalized on the server → it may refer to an already revoked one).
  const deviceLabel = (deviceId: string): string =>
    devices().find((d) => d.deviceId === deviceId)?.name ?? deviceId.slice(0, 8);

  const auditLabel = (action: AuditAction): string => {
    switch (action) {
      case "deposited": {
        return t(msg`Encrypted delivery received`);
      }
      case "picked_up": {
        return t(msg`Delivery collected`);
      }
      case "device_added": {
        return t(msg`Device authorized`);
      }
      case "device_revoked": {
        return t(msg`Device revoked`);
      }
      default: {
        return action;
      }
    }
  };

  onMount(() => {
    void (async () => {
      const acc = await loadLocalAccount();
      setAccount(acc);
      setLoaded(true);
      if (acc) {
        // The failure is already reported by the section itself (with its retry); duplicating it
        // in the notice above would only repeat the same message twice.
        await refresh(acc).catch(() => undefined);
      }
    })();
  });

  // Each handler runs its async work as an IIFE (same pattern as the
  // rest of the routes): homogeneous busy + error capture, no dangling promises.
  const finishWork = (promise: Promise<void>) => {
    setBusy(true);
    setError(undefined);
    void promise.catch((err: unknown) => setError(errorMessage(err))).finally(() => setBusy(false));
  };

  const createAccount = () => {
    finishWork(
      (async () => {
        const name = deviceName().trim() || t(msg`This device`);
        const acc = await createLocalAccount(name);
        setAccount(acc);
        await refresh(acc);
      })(),
    );
  };

  const showLinkCode = () => {
    finishWork(
      (async () => {
        const name = deviceName().trim() || t(msg`This device`);
        setLinkCode(await startDeviceLink(name));
      })(),
    );
  };

  const checkLink = () => {
    finishWork(
      (async () => {
        const acc = await completeDeviceLink(linkAddress());
        if (!acc) {
          setError(t(msg`Not linked yet — authorize this device from a trusted one first.`));
          return;
        }
        setAccount(acc);
        setLinkCode(undefined);
        await refresh(acc);
      })(),
    );
  };

  const copyAddress = () => {
    const acc = account();
    if (!acc) {
      return;
    }
    void (async () => {
      await navigator.clipboard.writeText(accountAddress(acc));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    })();
  };

  const authorizeDevice = () => {
    const acc = account();
    if (!acc) {
      return;
    }
    const payload = parseDeviceLinkCode(pasteCode());
    if (!payload) {
      setError(t(msg`That does not look like a device link code.`));
      return;
    }
    finishWork(
      (async () => {
        await addTrustedDevice(acc.accountId, accountAuth(acc), {
          sigPub: payload.sigPub,
          encPub: payload.encPub,
          deviceName: payload.name,
        });
        setPasteCode("");
        await refresh(acc);
      })(),
    );
  };

  const revokeDevice = (deviceId: string) => {
    const acc = account();
    if (!acc) {
      return;
    }
    finishWork(
      (async () => {
        await removeTrustedDevice(acc.accountId, accountAuth(acc), deviceId);
        await refresh(acc);
      })(),
    );
  };

  // "Check now" and the error state's retry are the SAME action:
  // the failure is painted in the section, so it is not duplicated in the notice above.
  const refreshInbox = () => {
    const acc = account();
    if (!acc) {
      return;
    }
    finishWork(refresh(acc).catch(() => undefined));
  };

  const collect = (meta: EnvelopeMeta) => {
    const acc = account();
    if (!acc) {
      return;
    }
    finishWork(
      (async () => {
        const file = await collectEnvelope(acc, meta);
        objectUrls.push(file.url);
        setCollected((prev) => [...prev, file]);
        setInbox((prev) => prev.filter((e) => e.id !== meta.id));
      })(),
    );
  };

  const forget = () => {
    forgetLocalAccount();
    setAccount(null);
    setDevices([]);
    setInbox([]);
    setCollected([]);
    setAudit([]);
    setLinkCode(undefined);
  };

  return (
    <main class="app-screen screen-enter mx-auto w-full max-w-2xl gap-6">
      <PageBackground />
      <TopBar />

      <h1
        class="font-display text-3xl tracking-tight text-foreground"
        data-testid="account-chrome-title"
      >
        {t(msg`Account & trusted devices`)}
      </h1>

      <Show when={error()}>
        {(message) => (
          <p
            data-testid="account-error"
            role="alert"
            aria-live="assertive"
            class="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            {message()}
          </p>
        )}
      </Show>

      <Show when={loaded()}>
        <Show
          when={account()}
          fallback={
            <div class="flex flex-col gap-6">
              <section class={panelClass}>
                <div class="flex items-center gap-2 text-accent">
                  <ShieldCheck class="h-5 w-5" />
                  <h2 class="font-display text-xl text-foreground">{t(msg`Create an account`)}</h2>
                </div>
                <p class="text-sm text-muted-foreground">
                  {t(
                    msg`Optional and anonymous: no email, no password. It unlocks trusted devices and an encrypted mailbox so others can leave you files while you are offline. Everything else keeps working without it.`,
                  )}
                </p>
                <label class="flex flex-col gap-2">
                  <span class="text-sm font-semibold text-foreground">{t(msg`Device name`)}</span>
                  <input
                    class={inputClass}
                    data-testid="input-device-name"
                    value={deviceName()}
                    onInput={(e) => setDeviceName(e.currentTarget.value)}
                    placeholder={t(msg`e.g. My phone`)}
                  />
                </label>
                <Button data-testid="btn-create-account" disabled={busy()} onClick={createAccount}>
                  {t(msg`Create account on this device`)}
                </Button>
              </section>

              <section class={panelClass}>
                <h2 class="font-display text-xl text-foreground">
                  {t(msg`Already have an account on another device?`)}
                </h2>
                <p class="text-sm text-muted-foreground">
                  {t(
                    msg`Generate this device's link code, paste it on a trusted device to authorize it, then verify with your account address.`,
                  )}
                </p>
                <Button
                  variant="outline"
                  data-testid="btn-start-link"
                  disabled={busy()}
                  onClick={showLinkCode}
                >
                  {t(msg`Show this device's link code`)}
                </Button>
                <Show when={linkCode()}>
                  {(code) => (
                    <>
                      <output
                        data-testid="device-link-code"
                        class="block w-full overflow-x-auto rounded-md border border-border bg-background px-3 py-2 font-mono text-xs text-foreground"
                      >
                        {code()}
                      </output>
                      <label class="flex flex-col gap-2">
                        <span class="text-sm font-semibold text-foreground">
                          {t(msg`Account address`)}
                        </span>
                        <input
                          class={inputClass}
                          data-testid="input-link-address"
                          value={linkAddress()}
                          onInput={(e) => setLinkAddress(e.currentTarget.value)}
                          placeholder={`${ACCOUNT_ADDRESS_PREFIX}…`}
                        />
                      </label>
                      <Button data-testid="btn-complete-link" disabled={busy()} onClick={checkLink}>
                        {t(msg`Verify link`)}
                      </Button>
                    </>
                  )}
                </Show>
              </section>
            </div>
          }
        >
          {(acc) => (
            <div class="flex flex-col gap-6">
              <section class={panelClass}>
                <h2 class="font-display text-xl text-foreground">{t(msg`Your address`)}</h2>
                <p class="text-sm text-muted-foreground">
                  {t(
                    msg`Share it with anyone who should be able to leave you files. Only your devices can decrypt them.`,
                  )}
                </p>
                <div class="flex items-center gap-2">
                  <output
                    data-testid="account-address"
                    class="block flex-1 overflow-x-auto rounded-md border border-border bg-background px-3 py-2 font-mono text-xs text-foreground"
                  >
                    {accountAddress(acc())}
                  </output>
                  <Button
                    variant="outline"
                    size="icon"
                    data-testid="btn-copy-address"
                    aria-label={t(msg`Copy address`)}
                    onClick={copyAddress}
                  >
                    {copied() ? <Check class="h-4 w-4" /> : <Copy class="h-4 w-4" />}
                  </Button>
                </div>
              </section>

              {/* Lo que vive en el RELAY. Si no contestó, aquí no se pinta ni
                  un vacío ni una lista: se pinta que no se pudo preguntar. */}
              <Show when={remote() === "error"}>
                <section
                  class={`${panelClass} border-destructive/40`}
                  data-testid="mailbox-connection-error"
                  role="alert"
                  aria-live="assertive"
                >
                  <div class="flex items-center gap-2 text-destructive">
                    <CloudOff class="h-5 w-5" />
                    <h2 class="font-display text-xl text-foreground">
                      {t(msg`Could not reach your mailbox`)}
                    </h2>
                  </div>
                  <p class="text-sm text-muted-foreground">
                    {remoteError() ?? t(msg`The mailbox service is unreachable right now.`)}
                  </p>
                  <p class="text-sm text-muted-foreground">
                    {t(
                      msg`Your deliveries and trusted devices are not shown because we could not ask — not because there are none. Nothing on this device was lost.`,
                    )}
                  </p>
                  <Button
                    variant="outline"
                    data-testid="mailbox-connection-retry"
                    disabled={busy()}
                    onClick={refreshInbox}
                    class="gap-2 self-start"
                  >
                    <RefreshCw class="h-4 w-4" />
                    {t(msg`Try again`)}
                  </Button>
                </section>
              </Show>

              <Show when={remote() !== "error"}>
                <section class={panelClass}>
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2 text-accent">
                      <Inbox class="h-5 w-5" />
                      <h2 class="font-display text-xl text-foreground">{t(msg`Mailbox`)}</h2>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="btn-refresh-inbox"
                      disabled={busy()}
                      onClick={refreshInbox}
                      class="gap-2"
                    >
                      <RefreshCw class="h-4 w-4" />
                      {t(msg`Check now`)}
                    </Button>
                  </div>
                  <Show when={remote() === "loading"}>
                    <p class="text-sm text-muted-foreground" data-testid="mailbox-loading">
                      {t(msg`Checking your mailbox…`)}
                    </p>
                  </Show>
                  <Show
                    when={remote() !== "loading" && (inbox().length > 0 || collected().length > 0)}
                    fallback={
                      <Show when={remote() !== "loading"}>
                        <p class="text-sm text-muted-foreground" data-testid="inbox-empty">
                          {t(
                            msg`No pending deliveries. Files left for you while offline show up here.`,
                          )}
                        </p>
                      </Show>
                    }
                  >
                    <ul class="flex flex-col gap-2">
                      <For each={inbox()}>
                        {(meta) => (
                          <li
                            data-testid="inbox-envelope"
                            class="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2"
                          >
                            <span class="text-sm text-foreground">
                              {t(msg`Encrypted delivery`)} · {formatBytes(meta.bytes)}
                            </span>
                            <Button
                              size="sm"
                              data-testid="btn-collect"
                              disabled={busy()}
                              onClick={() => collect(meta)}
                            >
                              {t(msg`Collect`)}
                            </Button>
                          </li>
                        )}
                      </For>
                    </ul>
                    <Show when={collected().length > 0}>
                      <ul class="flex flex-col gap-2">
                        <For each={collected()}>
                          {(file) => (
                            <li class="flex items-center justify-between gap-3 rounded-md border border-success/30 bg-success/10 px-3 py-2">
                              <a
                                data-testid="inbox-file"
                                href={file.url}
                                download={file.name}
                                class="truncate text-sm font-medium text-foreground underline-offset-4 hover:underline"
                              >
                                {file.name}
                              </a>
                              <span class="shrink-0 text-xs text-muted-foreground">
                                {file.from ?? ""} {formatBytes(file.size)}
                              </span>
                            </li>
                          )}
                        </For>
                      </ul>
                    </Show>
                  </Show>
                </section>

                <section class={panelClass}>
                  <div class="flex items-center gap-2 text-accent">
                    <Laptop class="h-5 w-5" />
                    <h2 class="font-display text-xl text-foreground">{t(msg`Trusted devices`)}</h2>
                  </div>
                  <Show when={remote() === "loading"}>
                    <p class="text-sm text-muted-foreground" data-testid="devices-loading">
                      {t(msg`Checking your trusted devices…`)}
                    </p>
                  </Show>
                  <ul class="flex flex-col gap-2">
                    <For each={remote() === "loading" ? [] : devices()}>
                      {(device) => (
                        <li
                          data-testid="device-row"
                          class="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2"
                        >
                          <span class="flex min-w-0 flex-col">
                            <span class="truncate text-sm font-medium text-foreground">
                              {device.name}
                              <Show when={device.deviceId === acc().deviceId}>
                                <span class="ml-2 rounded-full bg-accent/10 px-2 py-0.5 text-xs text-accent">
                                  {t(msg`this device`)}
                                </span>
                              </Show>
                            </span>
                            <span class="font-mono text-xs text-muted-foreground">
                              {device.deviceId}
                            </span>
                          </span>
                          <Show when={device.deviceId !== acc().deviceId}>
                            <Button
                              variant="outline"
                              size="icon"
                              data-testid="btn-revoke-device"
                              aria-label={t(msg`Revoke device`)}
                              disabled={busy()}
                              onClick={() => revokeDevice(device.deviceId)}
                            >
                              <Trash2 class="h-4 w-4" />
                            </Button>
                          </Show>
                        </li>
                      )}
                    </For>
                  </ul>
                  <label class="flex flex-col gap-2">
                    <span class="text-sm font-semibold text-foreground">
                      {t(msg`Authorize a new device`)}
                    </span>
                    <textarea
                      class="min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      data-testid="input-device-link-code"
                      value={pasteCode()}
                      onInput={(e) => setPasteCode(e.currentTarget.value)}
                      placeholder={`${DEVICE_LINK_CODE_PREFIX}…`}
                    />
                  </label>
                  <Button
                    variant="outline"
                    data-testid="btn-authorize-device"
                    disabled={busy() || pasteCode().trim() === ""}
                    onClick={authorizeDevice}
                  >
                    {t(msg`Authorize device`)}
                  </Button>
                </section>

                <section class={panelClass}>
                  <div class="flex items-center gap-2 text-accent">
                    <ScrollText class="h-5 w-5" />
                    <h2 class="font-display text-xl text-foreground">{t(msg`Transfer log`)}</h2>
                  </div>
                  <p class="text-sm text-muted-foreground">
                    {t(
                      msg`An audit trail of your mailbox: deposits, pickups and device changes. Metadata only — the relay never sees file contents.`,
                    )}
                  </p>
                  <Show when={remote() === "loading"}>
                    <p class="text-sm text-muted-foreground" data-testid="audit-loading">
                      {t(msg`Loading your transfer log…`)}
                    </p>
                  </Show>
                  <Show
                    when={remote() !== "loading" && audit().length > 0}
                    fallback={
                      <Show when={remote() !== "loading"}>
                        <p class="text-sm text-muted-foreground" data-testid="audit-empty">
                          {t(msg`No activity yet.`)}
                        </p>
                      </Show>
                    }
                  >
                    <ul class="flex flex-col gap-2" data-testid="audit-list">
                      <For each={audit()}>
                        {(entry) => (
                          <li
                            data-testid="audit-entry"
                            data-action={entry.action}
                            class="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2"
                          >
                            <span class="flex min-w-0 flex-col">
                              <span class="truncate text-sm font-medium text-foreground">
                                {auditLabel(entry.action)}
                              </span>
                              <span class="truncate text-xs text-muted-foreground">
                                {deviceLabel(entry.deviceId)}
                                <Show when={entry.bytes !== undefined}>
                                  {" · "}
                                  {formatBytes(entry.bytes ?? 0)}
                                </Show>
                              </span>
                            </span>
                            <span class="shrink-0 text-xs text-muted-foreground">
                              {new Date(entry.atUnix * 1000).toLocaleString()}
                            </span>
                          </li>
                        )}
                      </For>
                    </ul>
                  </Show>
                </section>
              </Show>

              <section class={panelClass}>
                <h2 class="font-display text-xl text-foreground">
                  {t(msg`Forget on this device`)}
                </h2>
                <p class="text-sm text-muted-foreground">
                  {t(
                    msg`Deletes this device's keys locally. Without another trusted device, the account cannot be recovered.`,
                  )}
                </p>
                <Button variant="destructive" data-testid="btn-forget-account" onClick={forget}>
                  {t(msg`Forget account here`)}
                </Button>
              </section>
            </div>
          )}
        </Show>
      </Show>
    </main>
  );
}

export const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  component: AccountPage,
});
